import type { FeatureCollection } from 'geojson';

/** Thrown when the input isn't well-formed XML (DOMParser reports a <parsererror>). */
export class GpxParseError extends Error {}

/** Parses one lon/lat pair from a gpx point element, or null when either is missing/invalid. */
function readLonLat(pt: Element): [number, number] | null {
  const lonAttr = pt.getAttribute('lon');
  const latAttr = pt.getAttribute('lat');
  if (lonAttr === null || latAttr === null) return null;
  const lon = parseFloat(lonAttr);
  const lat = parseFloat(latAttr);
  // Skip garbage rather than defaulting to 0 — a missing coordinate used to land the point
  // on Null Island (0,0), silently corrupting the route with a spike to the Atlantic.
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return [lon, lat];
}

export function parseGPX(text: string): FeatureCollection {
  const doc = new DOMParser().parseFromString(text, 'text/xml');
  // DOMParser never throws on malformed XML — it returns a document containing a
  // <parsererror> element instead. Detect that so a bad file surfaces as an error rather
  // than an empty (silent) result.
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new GpxParseError('This file is not valid GPX/XML.');
  }

  // GPX files declare a default namespace — getElementsByTagNameNS handles this reliably;
  // querySelectorAll on namespaced XML is inconsistent across browsers
  const ns = doc.documentElement.namespaceURI ?? '*';
  const get = (parent: Element | Document, tag: string) =>
    Array.from(parent.getElementsByTagNameNS(ns, tag));

  const features: FeatureCollection['features'] = [];

  // trkseg → one LineString per segment
  get(doc, 'trkseg').forEach((seg) => {
    const coords = get(seg, 'trkpt')
      .map(readLonLat)
      .filter((c): c is [number, number] => c !== null);
    if (coords.length)
      features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} });
  });

  // rte → one LineString per route
  get(doc, 'rte').forEach((rte) => {
    const coords = get(rte, 'rtept')
      .map(readLonLat)
      .filter((c): c is [number, number] => c !== null);
    if (coords.length)
      features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} });
  });

  // wpt → Point features; the name rides along so route vias keep their labels
  get(doc, 'wpt').forEach((wpt) => {
    const coords = readLonLat(wpt);
    if (!coords) return;
    const name = get(wpt, 'name')[0]?.textContent?.trim();
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: coords },
      properties: name ? { name } : {},
    });
  });

  return { type: 'FeatureCollection', features };
}
